import type { FeedItem } from '@knocklabs/client';
import { RiAlertLine, RiArrowRightLine, RiCheckLine, RiGridLine, RiSparklingFill } from '@remixicon/react';
import { NotificationType } from '@shared/types';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { navigateHref } from 'src/lib/routing';
import { appLinks } from 'src/routing/appLinks';

import { AiGradientIcon, relativeTime } from './notificationCell.helpers';

interface OrderData {
  type?: string;
  order_slug?: string;
  order_path?: string;
  customer_name?: string;
  customer_po?: string;
  total_revenue?: number;
  units?: number;
  pallets?: number;
  unmapped_products?: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function isAI(data: OrderData | null): boolean {
  return data?.type === NotificationType.AI_ORDER_CREATED;
}

interface OrderNotificationCellProps {
  item: FeedItem;
  onMarkAsRead?: () => void;
}

export default function OrderNotificationCell({ item, onMarkAsRead }: OrderNotificationCellProps) {
  const navigate = useNavigate();
  const router = useRouter();
  const data = item.data as OrderData | null;
  const isBatched = (item.total_activities ?? 0) > 1 && (item.activities?.length ?? 0) > 1;
  const isRead = !!item.read_at;

  const handleClick = () => {
    if (isBatched) {
      void navigate(appLinks.salesOrders());
    } else if (data?.order_path) {
      void navigateHref(router, data.order_path);
    }
  };

  if (isBatched) {
    return <BatchedNotification item={item} isRead={isRead} onClick={handleClick} onMarkAsRead={onMarkAsRead} />;
  }

  return (
    <SingleNotification item={item} data={data} isRead={isRead} onClick={handleClick} onMarkAsRead={onMarkAsRead} />
  );
}

// ─── Single notification ───────────────────────────────────────

function SingleNotification({
  item,
  data,
  isRead,
  onClick,
  onMarkAsRead,
}: {
  item: FeedItem;
  data: OrderData | null;
  isRead: boolean;
  onClick: () => void;
  onMarkAsRead?: () => void;
}) {
  const ai = isAI(data);
  const hasUnmapped = (data?.unmapped_products ?? 0) > 0;

  return (
    <div
      onClick={onClick}
      className="group relative flex flex-row items-start px-5 py-4 gap-3 cursor-pointer hover:bg-neutral-2"
    >
      {/* Unread dot */}
      {!isRead && <span className="absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-green-600" />}

      {/* Avatar/Icon badge */}
      <div className="flex items-center justify-center w-8 h-8 rounded-full border border-neutral-5 bg-white shrink-0">
        {ai ? (
          <AiGradientIcon>
            <RiSparklingFill size={16} />
          </AiGradientIcon>
        ) : (
          <RiGridLine size={16} className="text-blue-text" />
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        {/* Row 1: slug + "created for" + customer */}
        <div className="flex items-center gap-1.5">
          <span className="text-14 font-medium leading-5 tracking-[-0.1px] text-gray-900">{data?.order_slug}</span>
          <span className="text-14 leading-5 tracking-[-0.1px] text-neutral-alpha-9">created for</span>
          <span className="text-14 font-medium leading-5 tracking-[-0.1px] text-gray-900">{data?.customer_name}</span>
        </div>

        {/* Row 2: customer • revenue • units */}
        <div className="flex items-center gap-2">
          <span className="text-14 font-medium leading-5 tracking-[-0.1px] text-gray-900">{data?.customer_name}</span>
          {(data?.total_revenue ?? 0) > 0 && (
            <>
              <span className="w-1.5 h-1.5 rounded-full bg-brand-3" />
              <span className="text-14 font-medium leading-5 tracking-[-0.1px] text-gray-900">
                {formatCurrency(data!.total_revenue!)}
              </span>
            </>
          )}
          <span className="w-1.5 h-1.5 rounded-full bg-brand-3" />
          <span className="text-12 leading-4 text-neutral-alpha-9">{data?.units ?? 0} units</span>
        </div>

        {/* Row 3: Unmapped warning */}
        {hasUnmapped && (
          <div className="flex items-center gap-1">
            <RiAlertLine size={14} className="text-amber-500" />
            <span className="text-12 leading-4 font-medium text-amber-600">
              {data!.unmapped_products} product{data!.unmapped_products! > 1 ? 's' : ''} need mapping
            </span>
          </div>
        )}
      </div>

      {/* Right column: time + actions */}
      <div className="flex flex-col items-end justify-between gap-2 shrink-0">
        <span className="text-12 leading-4 text-neutral-alpha-9 text-right">{relativeTime(item.inserted_at)}</span>
        <div className="flex items-center gap-1">
          {!isRead && onMarkAsRead && (
            <button
              title="Mark as read"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead();
              }}
              className="hidden group-hover:flex items-center justify-center w-6 h-6 rounded-lg hover:bg-black/8 text-neutral-alpha-9 hover:text-gray-900"
            >
              <RiCheckLine size={14} />
            </button>
          )}
          <button className="flex items-center justify-center px-2 h-6 gap-1 rounded-lg bg-black/4">
            <span className="text-12 font-medium text-gray-900">View order</span>
            <RiArrowRightLine size={14} className="text-neutral-alpha-9" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Batched notification ──────────────────────────────────────

function BatchedNotification({
  item,
  isRead,
  onClick,
  onMarkAsRead,
}: {
  item: FeedItem;
  isRead: boolean;
  onClick: () => void;
  onMarkAsRead?: () => void;
}) {
  const activities = item.activities ?? [];
  const count = item.total_activities ?? activities.length;

  const firstData = (activities[0]?.data ?? item.data) as OrderData | null;
  const ai = isAI(firstData);

  const totalRevenue = activities.reduce((sum, a) => sum + ((a.data as OrderData)?.total_revenue ?? 0), 0);
  const totalUnits = activities.reduce((sum, a) => sum + ((a.data as OrderData)?.units ?? 0), 0);
  const totalPallets = activities.reduce((sum, a) => sum + ((a.data as OrderData)?.pallets ?? 0), 0);

  return (
    <div
      onClick={onClick}
      className="group relative flex flex-row items-start px-5 py-4 gap-3 cursor-pointer hover:bg-neutral-2"
    >
      {/* Unread dot */}
      {!isRead && <span className="absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-green-600" />}

      {/* Avatar/Icon badge */}
      <div className="flex items-center justify-center w-8 h-8 rounded-full border border-neutral-5 bg-white shrink-0">
        {ai ? (
          <AiGradientIcon>
            <RiSparklingFill size={16} />
          </AiGradientIcon>
        ) : (
          <RiGridLine size={16} className="text-blue-text" />
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        {/* Row 1: Count */}
        <div className="flex items-center gap-1.5">
          <span className="text-14 font-medium leading-5 tracking-[-0.1px] text-gray-900">{count} new orders</span>
          <span className="text-14 leading-5 tracking-[-0.1px] text-neutral-alpha-9">created</span>
        </div>

        {/* Row 2: Aggregated stats */}
        <div className="flex items-center gap-2">
          {totalRevenue > 0 && (
            <span className="text-14 font-medium leading-5 tracking-[-0.1px] text-gray-900">
              {formatCurrency(totalRevenue)} total
            </span>
          )}
          {totalRevenue > 0 && totalUnits > 0 && <span className="w-1.5 h-1.5 rounded-full bg-brand-3" />}
          {totalUnits > 0 && <span className="text-12 leading-4 text-neutral-alpha-9">{totalUnits} units</span>}
          {totalPallets > 0 && (
            <>
              <span className="w-1.5 h-1.5 rounded-full bg-brand-3" />
              <span className="text-12 leading-4 text-neutral-alpha-9">{totalPallets} pallets</span>
            </>
          )}
        </div>
      </div>

      {/* Right column: time + action */}
      <div className="flex flex-col items-end justify-between gap-2 shrink-0">
        <span className="text-12 leading-4 text-neutral-alpha-9 text-right">{relativeTime(item.inserted_at)}</span>
        <div className="flex items-center gap-1">
          {!isRead && onMarkAsRead && (
            <button
              title="Mark as read"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead();
              }}
              className="hidden group-hover:flex items-center justify-center w-6 h-6 rounded-lg hover:bg-black/8 text-neutral-alpha-9 hover:text-gray-900"
            >
              <RiCheckLine size={14} />
            </button>
          )}
          <button className="flex items-center justify-center px-2 h-6 gap-1 rounded-lg bg-black/4">
            <span className="text-12 font-medium text-gray-900">View orders</span>
            <RiArrowRightLine size={14} className="text-neutral-alpha-9" />
          </button>
        </div>
      </div>
    </div>
  );
}
